import { useState, useEffect, useCallback } from 'react';
import { getFactureDetails } from '../services/airtable';
import { useToast } from '../contexts/ToastContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';

/**
 * useFactureDetails - Hook pour gérer les détails d'une facture
 *
 * Pour les factures manuelles (isManual === true) :
 *   - expose montantHTManual, tauxTVAManual, dateEmissionManual
 *   - handleSaveManuel() appelle saveFactureManuelle
 * Pour les factures auto :
 *   - comportement inchangé (lignes de détail + saveFactureDetails)
 *
 * @param {string} numeroFacture - Numéro de la facture
 * @param {boolean} isOpen - Si la modale est ouverte
 * @param {Function} onClose - Callback de fermeture
 * @param {Function} onSave - Callback après sauvegarde
 */
export function useFactureDetails(numeroFacture, isOpen, onClose, onSave) {
  const [details, setDetails] = useState([]);
  const [hasStoredDetails, setHasStoredDetails] = useState(false);
  const [isManual, setIsManual] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tauxTVA, setTauxTVA] = useState(0.2);
  const [editingIndex, setEditingIndex] = useState(null);
  const [intitule, setIntitule] = useState('');
  const [statut, setStatut] = useState('');

  // État spécifique aux factures manuelles
  const [montantHTManual, setMontantHTManual] = useState('');
  const [tauxTVAManual, setTauxTVAManual] = useState('20');
  const [dateEmissionManual, setDateEmissionManual] = useState('');

  const { showToast } = useToast();

  // Charger les détails
  const loadDetails = useCallback(async () => {
    if (!numeroFacture) return;

    setLoading(true);
    try {
      const data = await getFactureDetails(numeroFacture);

      setDetails(data.details || []);
      setHasStoredDetails(data.hasStoredDetails);
      setIsManual(data.isManual || false);
      setStatut(data.statut || '');

      if (data.tauxTVA !== undefined) {
        setTauxTVA(data.tauxTVA);
      }

      if (data.intitule) {
        setIntitule(data.intitule);
      } else if (data.mois && data.annee) {
        const moisNoms = [
          'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
          'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
        ];
        setIntitule(`Prestations du mois de ${moisNoms[data.mois - 1]} ${data.annee}`);
      }

      // Pré-remplir les champs manuels
      if (data.isManual) {
        setMontantHTManual(data.montantHT != null ? String(data.montantHT) : '');
        setTauxTVAManual(data.tauxTVA != null ? String(Math.round(data.tauxTVA * 100)) : '20');
        setDateEmissionManual(data.dateEmission || '');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showToast(error.message || 'Erreur lors du chargement des détails', 'error');
    } finally {
      setLoading(false);
    }
  }, [numeroFacture, showToast]);

  // Charger au montage
  useEffect(() => {
    if (isOpen && numeroFacture) {
      loadDetails();
    }
  }, [isOpen, numeroFacture, loadDetails]);

  // === HANDLERS LIGNES (factures auto) ===

  const handleAddLine = useCallback((type) => {
    const newLine = {
      numero_ligne: details.length + 1,
      type_ligne: type,
      consultant: '',
      client_final: '',
      nombre_jours: 0,
      description: '',
      montant: 0,
      isNew: true
    };
    setDetails(prev => [...prev, newLine]);
    setEditingIndex(details.length);
  }, [details.length]);

  const handleAddTempsLine = useCallback(() => handleAddLine('Temps'), [handleAddLine]);
  const handleAddAjustementLine = useCallback(() => handleAddLine('Ajustement'), [handleAddLine]);

  const handleDeleteLine = useCallback((index) => {
    setDetails(prev => {
      const newDetails = prev.filter((_, i) => i !== index);
      return newDetails.map((detail, i) => ({
        ...detail,
        numero_ligne: i + 1
      }));
    });
  }, []);

  const handleEditLine = useCallback((index) => {
    setEditingIndex(index);
  }, []);

  const handleSaveLine = useCallback(() => {
    setEditingIndex(null);
  }, []);

  const handleFieldChange = useCallback((index, field, value) => {
    setDetails(prev => {
      const newDetails = [...prev];
      let finalValue = value;

      if (field === 'montant' || field === 'nombre_jours') {
        finalValue = value === '' ? 0 : parseFloat(value) || 0;
      }

      newDetails[index] = {
        ...newDetails[index],
        [field]: finalValue
      };

      return newDetails;
    });
  }, []);

  // === CALCULS ===

  const calculateTotals = useCallback(() => {
    const montantHT = details.reduce((sum, detail) => sum + (parseFloat(detail.montant) || 0), 0);
    const tva = montantHT * tauxTVA;
    const montantTTC = montantHT + tva;
    return { montantHT, tva, montantTTC };
  }, [details, tauxTVA]);

  // === SAUVEGARDE FACTURE AUTO ===

  const handleSave = useCallback(async (validateFacture = false) => {
    setSaving(true);
    try {
      const response = await fetchWithAuth('/.netlify/functions/saveFactureDetails', {
        method: 'POST',
        body: JSON.stringify({ numeroFacture, details, tauxTVA, intitule, validateFacture })
      });

      const data = await response.json();

      if (response.ok) {
        const message = validateFacture
          ? 'Facture validée et prête à être envoyée'
          : 'Détails enregistrés avec succès';
        showToast(message, 'success');
        setHasStoredDetails(true);

        setTimeout(() => {
          onClose();
          if (onSave) onSave();
        }, 1500);
      } else {
        showToast(data.error || 'Erreur lors de l\'enregistrement', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showToast('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setSaving(false);
    }
  }, [numeroFacture, details, tauxTVA, intitule, showToast, onClose, onSave]);

  // === SAUVEGARDE FACTURE MANUELLE ===

  const handleSaveManuel = useCallback(async (validateFacture = false) => {
    setSaving(true);
    try {
      const response = await fetchWithAuth('/.netlify/functions/saveFactureManuelle', {
        method: 'POST',
        body: JSON.stringify({
          numeroFacture,
          intitule,
          dateEmission: dateEmissionManual,
          montantHT: parseFloat(montantHTManual) || 0,
          tauxTVA: parseFloat(tauxTVAManual) || 0,
          validateFacture
        })
      });

      const data = await response.json();

      if (response.ok) {
        const message = validateFacture
          ? 'Facture validée et prête à être envoyée'
          : 'Facture enregistrée avec succès';
        showToast(message, 'success');

        setTimeout(() => {
          onClose();
          if (onSave) onSave();
        }, 1500);
      } else {
        showToast(data.error || 'Erreur lors de l\'enregistrement', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showToast('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setSaving(false);
    }
  }, [numeroFacture, intitule, dateEmissionManual, montantHTManual, tauxTVAManual, showToast, onClose, onSave]);

  // === FERMETURE ===

  const handleClose = useCallback(() => {
    setDetails([]);
    setHasStoredDetails(false);
    setIsManual(false);
    setEditingIndex(null);
    setIntitule('');
    setStatut('');
    setMontantHTManual('');
    setTauxTVAManual('20');
    setDateEmissionManual('');
    onClose();
  }, [onClose]);

  // === VALEURS CALCULÉES ===

  const shouldShowNoDetails = !hasStoredDetails && (statut === 'Emise' || statut === 'Payée');
  const effectiveDetails = shouldShowNoDetails ? [] : details;
  const totals = shouldShowNoDetails ? { montantHT: 0, tva: 0, montantTTC: 0 } : calculateTotals();
  const tempsDetails = effectiveDetails.filter(d => d.type_ligne === 'Temps');
  const ajustementDetails = effectiveDetails.filter(d => d.type_ligne === 'Ajustement');

  return {
    // État
    details,
    effectiveDetails,
    tempsDetails,
    ajustementDetails,
    hasStoredDetails,
    isManual,
    loading,
    saving,
    tauxTVA,
    setTauxTVA,
    editingIndex,
    intitule,
    setIntitule,
    statut,
    totals,
    shouldShowNoDetails,

    // État facture manuelle
    montantHTManual,
    setMontantHTManual,
    tauxTVAManual,
    setTauxTVAManual,
    dateEmissionManual,
    setDateEmissionManual,

    // Handlers
    handleAddTempsLine,
    handleAddAjustementLine,
    handleDeleteLine,
    handleEditLine,
    handleSaveLine,
    handleFieldChange,
    handleSave,
    handleSaveManuel,
    handleClose
  };
}
